import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/formatters';
import {
  PTO_STATUS_BADGE,
  PTO_STATUS_LABELS,
  formatDays,
  formatLeaveDetail,
} from '@/features/pto/format';
import type { PtoRequestWithPeople } from '@/features/pto/hooks';
import type { PtoStatus } from '@/types/database';

// Local YYYY-MM-DD helpers — same approach as ProjectTaskCalendar / CalendarPage.
function toKey(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function parseISODate(iso: string): Date {
  return new Date(`${iso}T00:00:00`);
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_LABEL = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
});

/** Statuses plotted on the team calendar (reject/cancel stay in the table only). */
const CALENDAR_STATUSES: ReadonlySet<PtoStatus> = new Set(['pending', 'approved']);

const CHIP_STYLES: Record<'pending' | 'approved', string> = {
  pending:
    'border-atmosphere-dark/40 bg-atmosphere-dark/10 text-atmosphere-dark',
  approved:
    'border-bright-moss-dark/40 bg-bright-moss-dark/10 text-bright-moss-dark',
};

const MAX_CHIPS_PER_DAY = 3;

export interface PtoCalendarEntry {
  request: PtoRequestWithPeople;
  /** Day within the request's inclusive range. */
  dateKey: string;
}

/**
 * Expand each pending/approved request across every calendar day it covers
 * so multi-day leave shows up on each day of the span.
 */
export function expandPtoRequestsToDays(
  requests: PtoRequestWithPeople[],
): Map<string, PtoCalendarEntry[]> {
  const map = new Map<string, PtoCalendarEntry[]>();
  for (const request of requests) {
    if (!CALENDAR_STATUSES.has(request.status)) continue;
    const start = parseISODate(request.start_date);
    const end = parseISODate(request.end_date);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;
    if (end < start) continue;
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) {
      const key = toKey(d);
      const entry: PtoCalendarEntry = { request, dateKey: key };
      const list = map.get(key);
      if (list) list.push(entry);
      else map.set(key, [entry]);
    }
  }
  for (const list of map.values()) {
    list.sort((a, b) => {
      const nameA =
        a.request.requester?.full_name ?? a.request.requester?.email ?? '';
      const nameB =
        b.request.requester?.full_name ?? b.request.requester?.email ?? '';
      return nameA.localeCompare(nameB);
    });
  }
  return map;
}

function personLabel(r: PtoRequestWithPeople): string {
  return r.requester?.full_name ?? r.requester?.email ?? 'Unknown';
}

function dateRangeLabel(r: Pick<PtoRequestWithPeople, 'start_date' | 'end_date'>): string {
  return r.start_date === r.end_date
    ? formatDate(r.start_date)
    : `${formatDate(r.start_date)} – ${formatDate(r.end_date)}`;
}

/**
 * Month grid of org-wide PTO — past, upcoming, and pending — for managers.
 * Month can be controlled by the parent (e.g. All PTO month filter).
 */
export function PtoTeamCalendar({
  requests,
  isLoading,
  month,
  onMonthChange,
}: {
  requests: PtoRequestWithPeople[];
  isLoading: boolean;
  /** First-of-month date controlling the visible grid. */
  month?: Date;
  onMonthChange?: (month: Date) => void;
}) {
  const [internalCursor, setInternalCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const cursor = month ?? internalCursor;
  const setCursor = (next: Date | ((prev: Date) => Date)) => {
    const value = typeof next === 'function' ? next(cursor) : next;
    const normalized = new Date(value.getFullYear(), value.getMonth(), 1);
    if (!month) setInternalCursor(normalized);
    onMonthChange?.(normalized);
  };
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const entriesByDay = useMemo(
    () => expandPtoRequestsToDays(requests),
    [requests],
  );

  const weeks = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const start = addDays(first, -first.getDay());
    const end = addDays(last, 6 - last.getDay());
    const days: Date[] = [];
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) {
      days.push(new Date(d));
    }
    const wk: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) wk.push(days.slice(i, i + 7));
    return wk;
  }, [cursor]);

  const todayStr = toKey(new Date());
  const selectedEntries = selectedDay
    ? (entriesByDay.get(selectedDay) ?? [])
    : [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Previous month"
            onClick={() =>
              setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))
            }
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const now = new Date();
              setCursor(new Date(now.getFullYear(), now.getMonth(), 1));
              setSelectedDay(null);
            }}
          >
            Today
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Next month"
            onClick={() =>
              setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))
            }
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <span className="min-w-36 text-sm font-medium text-foreground">
          {MONTH_LABEL.format(cursor)}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-bright-moss-dark" />
            Approved
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-atmosphere-dark" />
            Pending
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[760px] overflow-hidden rounded-lg border border-border bg-card">
          <div className="grid grid-cols-7 border-b border-border bg-secondary/40">
            {WEEKDAYS.map((d) => (
              <div
                key={d}
                className="px-2 py-2 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground"
              >
                {d}
              </div>
            ))}
          </div>

          {isLoading ? (
            <div className="grid grid-cols-7 gap-px bg-border">
              {Array.from({ length: 35 }).map((_, i) => (
                <div key={i} className="min-h-28 bg-card p-2">
                  <Skeleton className="mb-2 h-4 w-6" />
                  <Skeleton className="h-5 w-full" />
                </div>
              ))}
            </div>
          ) : (
            weeks.map((week, wi) => (
              <div
                key={wi}
                className={cn(
                  'grid grid-cols-7 gap-px bg-border',
                  wi > 0 && 'border-t border-border',
                )}
              >
                {week.map((day) => {
                  const key = toKey(day);
                  const inMonth = day.getMonth() === cursor.getMonth();
                  const isToday = key === todayStr;
                  const dayEntries = entriesByDay.get(key) ?? [];
                  const visible = dayEntries.slice(0, MAX_CHIPS_PER_DAY);
                  const overflow = dayEntries.length - visible.length;
                  const isSelected = selectedDay === key;

                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() =>
                        setSelectedDay((prev) => (prev === key ? null : key))
                      }
                      className={cn(
                        'min-h-28 space-y-1 p-1.5 text-left transition-colors',
                        inMonth
                          ? 'bg-card hover:bg-secondary/30'
                          : 'bg-secondary/20 hover:bg-secondary/40',
                        isSelected && 'ring-2 ring-inset ring-primary/40',
                      )}
                    >
                      <div className="flex items-center justify-between px-0.5">
                        <span
                          className={cn(
                            'flex h-6 w-6 items-center justify-center rounded-full text-xs',
                            isToday
                              ? 'bg-primary font-semibold text-primary-foreground'
                              : inMonth
                                ? 'text-foreground'
                                : 'text-muted-foreground/60',
                          )}
                        >
                          {day.getDate()}
                        </span>
                        {dayEntries.length > 0 && (
                          <span className="text-[10px] tabular-nums text-muted-foreground">
                            {dayEntries.length}
                          </span>
                        )}
                      </div>

                      {visible.map((entry) => (
                        <PtoChip key={entry.request.id} entry={entry} />
                      ))}
                      {overflow > 0 && (
                        <span className="block px-1 text-[10px] text-muted-foreground">
                          +{overflow} more
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>

      {selectedDay && (
        <div className="rounded-lg border border-border bg-secondary/20 p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-foreground">
              {formatDate(selectedDay)}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedDay(null)}
            >
              Close
            </Button>
          </div>
          {selectedEntries.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No time off on this day.
            </p>
          ) : (
            <ul className="space-y-2">
              {selectedEntries.map(({ request }) => {
                const detail = formatLeaveDetail(request);
                return (
                  <li
                    key={request.id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
                  >
                    <Badge variant={PTO_STATUS_BADGE[request.status]}>
                      {PTO_STATUS_LABELS[request.status]}
                    </Badge>
                    <span className="font-medium text-foreground">
                      {personLabel(request)}
                    </span>
                    <span className="whitespace-nowrap text-muted-foreground">
                      {dateRangeLabel(request)}
                    </span>
                    <span className="tabular-nums text-muted-foreground">
                      {formatDays(request.days)}
                    </span>
                    {detail !== '—' && (
                      <span className="truncate text-muted-foreground">
                        · {detail}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function PtoChip({ entry }: { entry: PtoCalendarEntry }) {
  const { request } = entry;
  const status = request.status === 'pending' ? 'pending' : 'approved';
  const name = personLabel(request);
  const title = [
    name,
    PTO_STATUS_LABELS[request.status],
    dateRangeLabel(request),
    formatDays(request.days),
    formatLeaveDetail(request),
  ]
    .filter((part) => part && part !== '—')
    .join(' · ');

  return (
    <span
      title={title}
      className={cn(
        'block w-full truncate rounded-md border px-1.5 py-0.5 text-[11px] leading-tight',
        CHIP_STYLES[status],
      )}
    >
      {name}
    </span>
  );
}
